import omit from 'lodash/omit';
import * as z from 'zod/v3';

import { makeBetterEnum } from '@watershed/constants/betterEnum';
import {
  BiChartKind,
  BiDisplayMappingType,
  BiFilterOperator,
  BiQueryMode,
  BiQueryOrderDirection,
  BiSavedViewVisibility,
  EmptyStringType,
} from '@watershed/constants/bi';
import { Flags } from '@watershed/constants/flags';
import {
  GQBiNormalizedMeasureInput,
  GQBiQueryContextInput,
  GQBiQuerySingleMeasureSelectorInput,
  GQBiSavedView,
  GQBiSavedViewQueryContextInput,
  GQCreateBiSavedViewInput,
  GQUpdateBiSavedViewInput,
} from '@watershed/shared-universal/generated/graphql-schema-types';
import { FootprintKind } from '@watershed/shared-universal/utils/constants';
import { ArrayifyUnion } from '@watershed/shared-util/types/ArrayifyUnion';
import { Collapse } from '@watershed/shared-util/types/Collapse';
import type { Expression } from '@watershed/bi-core';
import { Eval } from '@watershed/shared-util/types/Eval';
import { Satisfies } from '@watershed/shared-util/types/Satisfies';
// eslint-disable-next-line no-restricted-imports
import {
  biPrimitiveValueNonNullSchema,
  GQBiDataRow,
  GQBiRawArrayType,
  GQBiRawSingularType,
  GQBiRawValueType,
  GQBiSingularPrimitiveNonNullValue,
} from '../customScalarGqlTypes';
import { YMInterval } from '../utils/YearMonth';
import { getZodEnumValuesFromEnum } from '../utils/zod';
import { ymIntervalSchema } from '../zodSchemas/utilSchemas';

export const BiFieldTypeSchema = z.enum([
  'string',
  'number',
  'boolean',
  'date',
  'yearmonth',
]);

export type BiSingularPrimitiveNonNullValue = GQBiSingularPrimitiveNonNullValue;
export type BiRawSingularType = GQBiRawSingularType | undefined;
export type BiRawArrayType = GQBiRawArrayType;
export type BiRawValueType = GQBiRawValueType | undefined;
export type BiDataRow = GQBiDataRow;

export const BiFieldType = BiFieldTypeSchema.enum;
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type BiFieldType = z.infer<typeof BiFieldTypeSchema>;

export type BiFieldTypeToPrimitiveType = Satisfies<
  Record<BiFieldType, BiSingularPrimitiveNonNullValue>,
  {
    [BiFieldType.string]: string;
    [BiFieldType.number]: number;
    [BiFieldType.boolean]: boolean;
    [BiFieldType.date]: number;
    [BiFieldType.yearmonth]: number;
  }
>;

export const FieldMetaTypeConditioningPropertiesConst = [
  'isMultiValue',
  'type',
  'fieldId',
] as const satisfies Array<keyof BiFieldMeta>;
export type FieldMetaTypeConditioningProperties =
  (typeof FieldMetaTypeConditioningPropertiesConst)[number];

type BiMeasureFieldMetaTypeInfo = Omit<
  Pick<BiMeasureFieldMeta, FieldMetaTypeConditioningProperties>,
  'fieldId'
>;

export type BiFieldMetaTypeInfo = Pick<
  BiFieldMeta,
  FieldMetaTypeConditioningProperties
>;

type BiFieldMetaToPrimitiveNonNull<M extends BiFieldMetaTypeInfo> =
  // when passed an 'as const' fieldmeta the isMultiValue key isn't there at all so we need this check because EVERYTHING extends unknown
  // so without this we fall through to the true | false | undefined case which is incorrect. unknown for this is equivalent to only undefined
  unknown extends M['isMultiValue']
    ? BiFieldTypeToPrimitiveType[M['type']]
    : true | false extends M['isMultiValue']
      ?
          | ArrayifyUnion<BiFieldTypeToPrimitiveType[M['type']]>
          | BiFieldTypeToPrimitiveType[M['type']]
      : M['isMultiValue'] extends true
        ? ArrayifyUnion<BiFieldTypeToPrimitiveType[M['type']]>
        : M['isMultiValue'] extends false
          ? BiFieldTypeToPrimitiveType[M['type']]
          : undefined extends M['isMultiValue']
            ? BiFieldTypeToPrimitiveType[M['type']]
            : never;

export type BiFieldMetaToPrimitiveType<M extends BiFieldMetaTypeInfo> =
  | BiFieldMetaToPrimitiveNonNull<M>
  | null
  | undefined;

/**
 * Bi-related gating feature flags.
 *
 * The enum here serves two purposes:
 * - Centralizes the set of feature flags related to BI (easier to track add/remove)
 * - Improves type-checking performance (not using a large enum for this base type)
 */
const BiFieldGatingFlags = [
  Flags.PricingFy25CsrdReportBuilderModuleTemp,
  Flags.BiDrilldownSupplierMeasures,
  Flags.BiDrilldownFacilitiesMeasures,
  Flags.AssetManagerRecordAttributionFactorOverride,
  Flags.BiShowKyotoGasEmissionsMetrics,
] as const;

export const biCommonFieldMetaSchema = z.object({
  fieldId: z.string(),
  displayName: z.string(),
  description: z.string().optional(),
  // controls the grouping of options in the select dropdown
  fieldFamily: z.string().optional(),
  // naming?
  hiddenInFilters: z.boolean().optional(),
  hiddenInGroupBy: z.boolean().optional(),
  // default is Empty
  emptyStringType: z.nativeEnum(EmptyStringType).optional(),
  isMultiValue: z.boolean().optional(),
  // If undefined, default to false
  isCustomField: z.boolean().optional(),
  /**
   * If undefined, viewable by everyone
   * Define these in {@link BiFieldGatingFlags}
   */
  gatingFeatureFlag: z.enum(BiFieldGatingFlags).optional(),
});

export type BiCommonFieldMeta = z.infer<typeof biCommonFieldMetaSchema>;

const baseListOptionItemSchema = z.object({
  label: z.string().nullable(),
});
export const dimensionListOptionItemSchema = baseListOptionItemSchema.extend({
  value: z.union([z.string(), z.boolean()]).nullable(),
});
export const stringDimensionListOptionItemSchema =
  baseListOptionItemSchema.extend({
    value: z.string().nullable(),
  });

export const biStringFieldMetaSchema = biCommonFieldMetaSchema.merge(
  z.object({
    type: z.literal(BiFieldType.string),
    shouldSentenceCaseValues: z.boolean().optional(),
    shouldNullifyPseudoNulls: z.boolean().optional(),
    hrefTemplate: z.string().optional(), // this must be serialized, so assume it is templated with __value__
    knownDisplayValueMappingType: z.nativeEnum(BiDisplayMappingType).optional(),
    staticListOptions: z.array(stringDimensionListOptionItemSchema).optional(),
  })
);
export type BiStringFieldMeta = z.infer<typeof biStringFieldMetaSchema>;

export interface IntensityMetadata {
  numerator: number;
  denominator: number;
}

export interface BiCalculatedValue<T extends BiRawValueType = BiRawValueType> {
  value: T;
  metadata: {
    intensity?: IntensityMetadata;
  };
}

export type NumericValueWithMetadata = BiCalculatedValue<
  number | null | undefined
>;

/**
 * Format of BI timeseries data. We are keeping this in shared-universal because
 * anomaly detection needs it.
 */
export type FormattedTimeSeriesDataRow = {
  measure: NumericValueWithMetadata;
  timeRaw: BiRawValueType;
  groupId: string;
  groupFormatted: string;
};

// We can't use z.nativeEnum(BiFilterOperator)
// here because doing the following in ConditionFormFragment.tsx
// does not work:
//   ComponentProps<
//    typeof ZodFormFragment<typeof biQueryFilterStringValuesSchema>
//   >
const biFilterOperatorSchema = z.enum(
  getZodEnumValuesFromEnum(BiFilterOperator)
);
export const biQueryFilterSchemaRaw = z.object({
  dimension: z.string(),
  // TODO: technically this is not a mixed type but iteration of
  // the union on the actual array makes typing very annoying
  // TODO: with the addition of operators such as "less than"
  // we should support single values as well instead of having
  // arrays of length 1.
  value: z.array(biPrimitiveValueNonNullSchema.nullable()),
  operator: biFilterOperatorSchema,
});

export interface BiQueryFilterSchema {
  dimension: string;
  value: Array<typeof biPrimitiveValueNonNullSchema._type | null>;
  operator: typeof biFilterOperatorSchema._type;
}
// Need to explicitly type cast this to prevent an error where the inferred type of
// the ReportComponentSchemas node exceeds the maximum length the compiler will
// serialized. This is just a workaround. We need to come up with a more permanent fix.
// The biQueryFilterSchemaRaw version is used in various places to construct derived schemas
export const biQueryFilterSchema: z.ZodSchema<BiQueryFilterSchema> =
  biQueryFilterSchemaRaw;

export const biArrayQueryFilterSchema = z.array(biQueryFilterSchema);

export type BiQueryFilter = z.TypeOf<typeof biQueryFilterSchemaRaw>;
export type BiQueryFilters = z.TypeOf<typeof biArrayQueryFilterSchema>;

/** Enum representing different types of BI filters */
export enum BiFilterType {
  DimensionOpValue = 'DimensionOpValue',
  Expression = 'Expression',
}

/**
 * - sum:
 * Suitable for summable quantities like kg of carbon.
 * Results in sum() sql aggregate function.
 *
 * - avg:
 * Suitable for quantities that must be averaged regardless of the
 * grouping dimensions e.g. employee salary, hourly rate.
 * Results in avg() sql aggregate function.
 *
 * - monthlyAvg:
 * Suitable for "gauges" like employee headcount or building sqft.
 * If 'month' is one of the grouping dimensions, behaves like 'sum'.
 * If 'month' is not one of the dimensions, we will first aggregate
 * with a 'sum' function across all grouping dimensions plus 'month',
 * then we aggregate the resulting summed quantity with an 'avg'
 * function across all grouping dimensions but not month. E.g.
 *
 * with inner as (
 *   select month, country, sum(headcount) as headcount
 *   from employee_data group by month, country
 * )
 * select country, avg(headcount) as headcount
 * from inner group by country
 */
export const AggregateMethod = {
  Avg: 'avg',
  CountDistinct: 'countDistinct',
  MonthlyAvg: 'monthlyAvg',
  Sum: 'sum',
} as const;
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type AggregateMethod =
  (typeof AggregateMethod)[keyof typeof AggregateMethod];

export const biNumberFieldMetaSchema = biCommonFieldMetaSchema.merge(
  z.object({
    type: z.literal(BiFieldType.number),
    aggregateType: z.nativeEnum(AggregateMethod).optional(),

    precision: z.number().optional(),
    decimalPlaces: z.number().optional(),
    disableCommas: z.boolean().optional(),
    includeUnit: z.boolean().optional(),
    shouldFormatToPercent: z.boolean().optional(),

    unit: z.string().optional(),
    unitDimension: z.string().optional(),
    dependentDimensions: z.array(z.string()).optional(),
    aggregatedUnitDimension: z.string().optional(),

    // Optional scaling factor by which underlying data should be divided before being returned.
    divideBy: z.number().optional(),
    unitUserFacing: z.string().optional(),
    isCurrency: z.boolean().optional(),
  })
);
export type BiNumberFieldMeta = z.infer<typeof biNumberFieldMetaSchema>;

export const biBooleanFieldMetaSchema = biCommonFieldMetaSchema.merge(
  z.object({
    type: z.literal(BiFieldType.boolean),
  })
);
export type BiBooleanFieldMeta = z.infer<typeof biBooleanFieldMetaSchema>;

const dateMonthOptions: z.ZodSchema<Intl.DateTimeFormatOptions['month']> =
  z.enum(['numeric', '2-digit', 'long', 'short', 'narrow']);
const dateDayOptions: z.ZodSchema<Intl.DateTimeFormatOptions['day']> = z.enum([
  'numeric',
  '2-digit',
]);
const dateYearOptions: z.ZodSchema<Intl.DateTimeFormatOptions['year']> = z.enum(
  ['numeric', '2-digit']
);
export const dateTimeFormatOptionsSchema = z.object({
  day: dateDayOptions.optional(),
  month: dateMonthOptions.optional(),
  year: dateYearOptions.optional(),
});

export const biDateFieldMetaSchema = biCommonFieldMetaSchema.merge(
  z.object({
    type: z.literal(BiFieldType.date),
    format: dateTimeFormatOptionsSchema.optional(),
  })
);
export type BiDateFieldMeta = z.infer<typeof biDateFieldMetaSchema>;

export const biYearMonthFieldMetaSchema = biCommonFieldMetaSchema.merge(
  z.object({
    type: z.literal(BiFieldType.yearmonth),
    format: dateTimeFormatOptionsSchema.optional(),
  })
);
export type BiYearMonthFieldMeta = z.infer<typeof biYearMonthFieldMetaSchema>;

export const biFieldMeta = z.discriminatedUnion('type', [
  biStringFieldMetaSchema,
  biNumberFieldMetaSchema,
  biBooleanFieldMetaSchema,
  biDateFieldMetaSchema,
  biYearMonthFieldMetaSchema,
]);
export type BiFieldMeta = z.infer<typeof biFieldMeta>;

export const biDimensionFieldMetaSchema = biFieldMeta;
export type BiDimensionFieldMeta = z.infer<typeof biDimensionFieldMetaSchema>;

export const biMeasureFieldMetaSchema = biNumberFieldMetaSchema.extend({
  isMultiValue: z.literal(false).optional(),
  filters: biArrayQueryFilterSchema.optional(),
});
export type BiMeasureFieldMeta = z.infer<typeof biMeasureFieldMetaSchema>;

export const biMeasureMetaSchema = z.object({
  // TODO: We may need this to be nullable, to indicate all dimensions are supported (e.g. for literal expressions)
  supportedDimensions: z.array(z.string()),
  fieldMeta: biMeasureFieldMetaSchema,
});
export type BiMeasureMeta = z.infer<typeof biMeasureMetaSchema>;

const dimensionComputedTypeSchema = z.enum([
  // this indicates that we will take the numerator value either dimensions or filters and intersect it's dimension values
  // with the supportedDimensions of the normalizingQuery measure
  // for dimensions this is obvious
  // for filters it means removing any filters whose dimension isn't the supportedDimensions of the normalizingQuery measure
  'NumeratorIntersection',
]);

export const biNormalizedMeasureSchema = z.object({
  baseMeasure: z.string(),
  // these filters are in addition to the overall query level filters
  // but only get applied to the numerator of the normalized measure
  baseFilters: biQueryFilterSchema.array().nullish(),
  normalizingQuery: z
    .intersection(
      z.intersection(
        z.object({
          measure: z.string(),
        }),
        z.union([
          z.object({
            dimensions: z.array(z.string()),
          }),
          z.object({
            computedDimensionType: dimensionComputedTypeSchema,
          }),
        ])
      ),
      z.union([
        z.object({
          filters: biQueryFilterSchema.array(),
        }),
        z.object({
          computedFiltersType: dimensionComputedTypeSchema,
        }),
      ])
    )
    .nullish(),
});

export const biArrayNormalizedMeasureSchema = z.array(
  biNormalizedMeasureSchema
);

export type BiNormalizedMeasure = z.TypeOf<typeof biNormalizedMeasureSchema>;

export const biCustomMetricRefSchema = z.object({
  id: z.string().nullable(),
  stableId: z.string(),
});

export type BiCustomMetricRef = z.infer<typeof biCustomMetricRefSchema>;

export const biArrayCustomMetricRefsSchema = z.array(biCustomMetricRefSchema);

export const biArrayQueryDimensionSchema = z.array(z.string());
export type BiDimensions = z.infer<typeof biArrayQueryDimensionSchema>;

export const biQueryOrderSchema = z.object({
  dimensionOrMeasure: z.string(),
  direction: z.nativeEnum(BiQueryOrderDirection),
});

export type BiOrder = z.infer<typeof biQueryOrderSchema>;

export const biSavedViewQuerySnapshotContext = z.object({
  mode: z.enum([BiQueryMode.Snapshot, BiQueryMode.FinanceFootprintSnapshot]),
  footprintKind: z.string(),
  footprintSnapshotIds: z.array(z.string()).nullish(),
});

const biSavedViewQueryProductFootprintContext = z.object({
  mode: z.literal(BiQueryMode.ProductFootprint),
});

const biSavedViewQueryMaterialMasterContext = z.object({
  mode: z.literal(BiQueryMode.MaterialMaster),
});

export const biSavedViewQueryContextSchema = z
  .discriminatedUnion('mode', [
    biSavedViewQuerySnapshotContext,
    biSavedViewQueryProductFootprintContext,
    biSavedViewQueryMaterialMasterContext,
  ])
  .nullable();

export type BiSavedViewQueryContextType = z.TypeOf<
  typeof biSavedViewQueryContextSchema
>;

export const biSavedViewDisplayConfigSchema = z.object({
  decimalPlaces: z.number().nullish(),
});

export type BiSavedViewDisplayConfigType = z.TypeOf<
  typeof biSavedViewDisplayConfigSchema
>;

/**
 * Common fields for all saved view schemas.
 */
export const biSavedViewBaseSchema = z.object({
  id: z.string(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
  deletedAt: z.date().nullish(),
  editVisibility: z.nativeEnum(BiSavedViewVisibility),
  viewVisibility: z.nativeEnum(BiSavedViewVisibility),
  name: z.string(),
  stableId: z.string(),
  lastVersionId: z.string().nullable(),
  orgId: z.string(),
  ownerId: z.string(),
  editorId: z.string(),
  mode: z.nativeEnum(BiQueryMode),
});

const legacyBiSavedViewStateSchema = z.object({
  order: biQueryOrderSchema.nullable(),
  chartKind: z.nativeEnum(BiChartKind),
  mode: z.nativeEnum(BiQueryMode),
  searchTerm: z.string().nullable(),
  queryContext: biSavedViewQueryContextSchema.nullable(),
  metricOrder: z.array(z.string()).nullish(),
  displayConfig: biSavedViewDisplayConfigSchema.nullish(),
  filters: biArrayQueryFilterSchema,
  dimensions: biArrayQueryDimensionSchema,
  normalizedMetrics: biArrayNormalizedMeasureSchema,
  timeIntervals: z.array(ymIntervalSchema),
  customMetrics: biArrayCustomMetricRefsSchema.nullable(),
});

/**
 * @deprecated Schema for legacy saved views (with individual record columns). Use biSavedViewSchema instead.
 */
export const biLegacySavedViewSchema = biSavedViewBaseSchema.extend({
  version: z.literal('bi:1'),
  normalizedMeasures: biArrayNormalizedMeasureSchema,
  order: biQueryOrderSchema.nullable(),
  chartKind: z.nativeEnum(BiChartKind),
  mode: z.nativeEnum(BiQueryMode),
  searchTerm: z.string().nullable(),
  queryContext: biSavedViewQueryContextSchema.nullable(),
  metricOrder: z.array(z.string()).nullish(),
  displayConfig: biSavedViewDisplayConfigSchema.nullish(),
  filters: biArrayQueryFilterSchema,
  dimensions: biArrayQueryDimensionSchema,
  timeIntervals: z.array(ymIntervalSchema),
  customMetrics: biArrayCustomMetricRefsSchema.nullable(),
  state: legacyBiSavedViewStateSchema,
});
export type BiLegacySavedViewType = z.TypeOf<typeof biLegacySavedViewSchema>;

export enum TemplateIcon {
  auto,
  calculator,
  calendar,
  city,
  cleanPower,
  factory,
  flash,
  friends,
  money,
  stack,
  arrow,
}

export enum SavedViewTemplateIntervalType {
  Full,
  MostRecent,
  TimeComparison,
}

export const biSavedViewBasePartialSchema = z.object({
  isDraft: z.boolean().optional().default(false),
  intervalType: z.nativeEnum(SavedViewTemplateIntervalType).optional(),
});

export const SAVED_VIEW_OMIT_FIELDS = {
  id: true,
  orgId: true,
  editorId: true,
  lastVersionId: true,
  createdAt: true,
  updatedAt: true,
} as const;

// Note: this is a subset of the full type for FE purposes only,
// excluding certain fields that we don't need on the client side
// and hydrating the normalized measures with their meta
export const legacyBiSavedViewPartialSchema = biLegacySavedViewSchema
  .omit({
    // use stableId instead
    ...SAVED_VIEW_OMIT_FIELDS,
  })
  .merge(biSavedViewBasePartialSchema);

export type LegacyBiSavedViewPartialType = z.TypeOf<
  typeof legacyBiSavedViewPartialSchema
>;

export type WatershedSavedViewType = Omit<
  LegacyBiSavedViewPartialType,
  'stableId' | 'ownerId' | 'isDraft'
> & {
  templateId: string;
  owner: {
    displayName: string;
  };
  icon: TemplateIcon;
  description: string;
  intervalType: SavedViewTemplateIntervalType;
};

export function dbQueryContextToGqlType(
  queryContext?: BiLegacySavedViewType['queryContext']
): GQBiSavedViewQueryContextInput | null {
  if (!queryContext) {
    return null;
  }
  // Note: we only support snapshot, product footprint, and material master queries in saved views for now
  if ('footprintKind' in queryContext) {
    return {
      mode: queryContext.mode,
      snapshot: {
        footprintKind: queryContext.footprintKind,
      },
    };
  } else if (queryContext.mode === BiQueryMode.ProductFootprint) {
    return { mode: BiQueryMode.ProductFootprint };
  } else if (queryContext.mode === BiQueryMode.MaterialMaster) {
    return { mode: BiQueryMode.MaterialMaster };
  }
  return null;
}

/**
 * Converts a database representation of a saved view to a
 * GraphQL representation for an insert operation.
 */
export function toGqlSavedViewCreateFields(
  view: WatershedSavedViewType | LegacyBiSavedViewPartialType
): GQCreateBiSavedViewInput {
  const fields: GQCreateBiSavedViewInput = {
    name: view.name,
    timeIntervals: view.timeIntervals,
    chartKind: view.chartKind,
    normalizedMeasures: view.normalizedMeasures,
    customMetrics: view.customMetrics ?? null,
    order: view.order,
    searchTerm: view.searchTerm,
    queryContext: dbQueryContextToGqlType(view.queryContext),
    dimensions: view.dimensions,
    filters: view.filters,
    editVisibility: view.editVisibility,
    viewVisibility: view.viewVisibility,
    mode: view.mode,
    metricOrder: view.metricOrder ?? null,
    version: view.version ?? 'bi:1',
    state: view.state,
  };
  return fields;
}

/**
 * Converts a database representation of a saved view to a
 * GraphQL representation for an update operation.
 */
export function toGqlSavedViewUpdateFields(
  view: LegacyBiSavedViewPartialType
): GQUpdateBiSavedViewInput {
  return {
    ownerId: view.ownerId,
    stableId: view.stableId,
    ...omit(toGqlSavedViewCreateFields(view), 'mode'),
  };
}

export type CustomReportInfo = {
  id: string;
  name: string;
  config: {
    id: string;
    updatedAt: Date;
  };
};

export function toGqlNormalizedMeasure(
  normalizedMeasure: BiNormalizedMeasure
): GQBiSavedView['normalizedMeasures'][number] {
  return {
    ...normalizedMeasure,
    baseFilters: normalizedMeasure.baseFilters
      ? normalizedMeasure.baseFilters
      : [],
    normalizingQuery: toGqlNormalizingQuery(normalizedMeasure.normalizingQuery),
  };
}

function toGqlNormalizingQuery(
  normalizingQuery: BiNormalizedMeasure['normalizingQuery'] | null
): GQBiSavedView['normalizedMeasures'][number]['normalizingQuery'] | null {
  if (!normalizingQuery) {
    return null;
  }
  return {
    measure: normalizingQuery.measure,
    dimensions:
      'dimensions' in normalizingQuery ? normalizingQuery.dimensions : null,
    filters: 'filters' in normalizingQuery ? normalizingQuery.filters : null,
    computedDimensionType:
      'computedDimensionType' in normalizingQuery
        ? normalizingQuery.computedDimensionType
        : null,
    computedFiltersType:
      'computedFiltersType' in normalizingQuery
        ? normalizingQuery.computedFiltersType
        : null,
  };
}

export function gqlQueryContextToDbType(
  queryContext?: GQBiQueryContextInput | GQBiSavedViewQueryContextInput | null
): BiLegacySavedViewType['queryContext'] {
  if (!queryContext) {
    return null;
  }
  // Note: we only support snapshot queries, product footprint queries, and material master queries in saved views for now
  if (
    queryContext?.snapshot &&
    (queryContext.mode === BiQueryMode.Snapshot ||
      queryContext.mode === BiQueryMode.FinanceFootprintSnapshot)
  ) {
    return {
      mode: queryContext.mode,
      footprintKind:
        queryContext.snapshot.footprintKind ?? FootprintKind.CorporateFootprint,
    };
  } else if (queryContext.mode === BiQueryMode.ProductFootprint) {
    return { mode: BiQueryMode.ProductFootprint };
  } else if (queryContext.mode === BiQueryMode.MaterialMaster) {
    return { mode: BiQueryMode.MaterialMaster };
  }
  return null;
}

const biMetricTypesSchema = z.enum(['Normalized', 'Custom']);

/** Strings representing expression types */
type BiMetricType = z.infer<typeof biMetricTypesSchema>;

/** Enum of expression types */
export const BiMetricTypes: { [T in BiMetricType]: T } =
  biMetricTypesSchema.enum;

export type BiMetricNormalized = {
  type: typeof BiMetricTypes.Normalized;
  meta: BiMeasureMeta;
  baseMeasureMeta: BiMeasureMeta;
  normalizingMeasureMeta?: BiMeasureMeta;
  normalizedMeasure: BiNormalizedMeasure;
};

export type BiMetricCustom = {
  type: typeof BiMetricTypes.Custom;
  meta: BiMeasureMeta;
  customMetric: BiCustomMetric;
};

export type BiMetric = BiMetricNormalized | BiMetricCustom;

export interface BiQueryComponents {
  filters: Array<BiQueryFilter>;
  dimensions: BiDimensions;
  metrics: Array<BiMetric>;
  timeIntervals: Array<YMInterval>;
  order?: Array<BiOrder> | undefined;
}

const biDimensionMetaSchema = z.object({
  fieldMeta: biDimensionFieldMetaSchema,
  // Supported dimensions exist here for dimension-based expressions like count_distinct
  supportedDimensions: z.array(z.string()).optional(),
});
export type BiDimensionMeta = z.infer<typeof biDimensionMetaSchema>;

export const biMetadataSchema = z.object({
  timeInterval: ymIntervalSchema.optional(),
  measures: biMeasureMetaSchema.array(),
  dimensions: z.array(biDimensionMetaSchema),
});
export type BiMetadata = z.infer<typeof biMetadataSchema>;

export interface BiSupportedMeasure {
  measure: string;
  supportedDenominators: Array<string>;
}

/** Model representing a custom metric */
export type BiCustomMetric = {
  id?: string;
  stableId?: string;
  name: string;
  expression: Expression;
  wshql?: string | null;
};
/** A custom metric that has been saved to the database  */
export type BiSavedCustomMetric = Required<BiCustomMetric>;

/**
 * Creates keys for use with hash and local storage atoms
 */
export const BiQueryComponentHashKey = {
  // V1 keys
  filters: biLocalStorageKey('filters'),
  dimensions: biLocalStorageKey('dimensions'),
  intervals: biLocalStorageKey('intervals'),
  measures: biLocalStorageKey('measures'),
  customMetrics: biLocalStorageKey('customMetrics'),
  metricOrder: biLocalStorageKey('metricOrder'),
  // V2 keys
  metricsV2: biLocalStorageKeyV2('metrics'),
  dimensionsV2: biLocalStorageKeyV2('dimensions'),
  filtersV2: biLocalStorageKeyV2('filters'),
  // Shared keys
  view: biLocalStorageKey('view'),
  footprintKind: biLocalStorageKey('footprintKind'),
  showCharts: biLocalStorageKey('showCharts'),
  chartKind: biLocalStorageKey('chartKind'),
  showTable: biLocalStorageKey('showTable'),
  tableKind: biLocalStorageKey('tableKind'),
  timeSeriesDimension: biLocalStorageKey('timeSeriesDimension'),
  barChartUseSecondDimension: biLocalStorageKey('barChartUseSecondDimension'),
  barChartLayout: biLocalStorageKey('barChartLayout'),
  barChartSortOrder: biLocalStorageKey('barChartSortOrder'),
} as const;

/**
 * Creates namespaced keys for use with local storage atoms
 */
function biLocalStorageKey(prefix: string): string {
  return `bi:${prefix}`;
}

/**
 * Creates namespaced keys for use with local storage atoms for V2 state components
 */
function biLocalStorageKeyV2(prefix: string): string {
  return `bi:v2:${prefix}`;
}

type FindByProp<T extends Array<any>, K extends keyof any, V> =
  T extends Array<infer U>
    ? U extends {
        [key in K]: V;
      }
      ? U
      : never
    : never;

export type TypedBiRow<M extends Array<BiFieldMetaTypeInfo>> = Eval<{
  [FieldId in M[number]['fieldId']]: BiFieldMetaToPrimitiveNonNull<
    // leaving the union inline here lets it expand rows down to things like
    // {tco2 : number| null | undefined} which is not what we want
    // note: weirdly unioning with null and undefined makes TS have a harder type expanded this
    // type with Eval
    // it would be nice to make this type more efficient
    // as is it's O(n^2) where n is number of field metas passed, which should be relatively small N but still
    // i tried iterating the indices of the tuple and directly mapping to fieldId and meta but TS loses the per key type of some reason when doing that
    // see: https://www.typescriptlang.org/play/?#code/C4TwDgpgBAqgdgSwPZygXigb2ALigcgC8IAnJfAGigEMo8BGAXwB8tcCUJKoAjOqAEyMA3AFgAUBNCQoASTgATBAGMIAZwA8AFSgQAHsAiK1NOCADaAXQB86KAFE9ygDYBXBRA0BrCCCQAzKC0qHz9A6jMrazFJcWloAEFtXQMjBRN4ZDgou0wJKAKocwBpKARUeSVVTS1bahMtEqgAMig4VwBbHlJLc3xgfEtLPGKYkQkpcGgAIWT9Q2NYRBQcjDzxQqLS8rlFFXVtOpNiy35iiUYJuKmoBIE7JPNsfiJScipaBkYqZ7x8Tm4fDwQhsEgA9AAqCQ8JDAAAWUFCJgA5hBgLxYQjivg1ABCXFQZEkagkXGTGR3XL5QrEMh4ABEAAZ6VBWPT6PSYptOAzmayoOzORcJBCweSZvcMLMnui-rT3jR+EwfrKOHAuFQgYJGKDxJDxVBppKsNSCozeUKNoV6AyOWMRWCgA
    FindByProp<M, 'fieldId', FieldId>
  > | null;
}>;

export type NormalizedMeasuresToMetas<
  M extends Array<GQBiNormalizedMeasureInput>,
> = {
  [K in M[number]['fieldId']]: Collapse<
    {
      fieldId: K;
    } & BiMeasureFieldMetaTypeInfo
  >;
}[M[number]['fieldId']];

// type A = TypedBiRow<
//   [
//     {
//       fieldId: 'tco2eQuantity';
//       type: 'number';
//       isMultiValue?: false;
//     },
//     {
//       fieldId: 'tco2eLocationQuantity';
//       type: 'number';
//       isMultiValue?: false;
//     },
//   ]
// >;

/**
 * Used for data lineage. Can be removed once footprintKind is removed from this GQL type.
 */
export type BiSingleMeasureSelector = Omit<
  GQBiQuerySingleMeasureSelectorInput,
  'footprintKind'
>;

export const CATEGORICAL_FILTER_OPERATORS = [
  BiFilterOperator.In,
  BiFilterOperator.NotIn,
] as const;

export const NUMERICAL_FILTER_OPERATORS = [
  BiFilterOperator.Equal,
  BiFilterOperator.NotEqual,
  BiFilterOperator.GreaterThan,
  BiFilterOperator.GreaterThanOrEqual,
  BiFilterOperator.LessThan,
  BiFilterOperator.LessThanOrEqual,
] as const;
export type NumericalFilterOperator =
  (typeof NUMERICAL_FILTER_OPERATORS)[number];

export function isNumericalFilterOperator(
  o: BiFilterOperator
): o is NumericalFilterOperator {
  return NUMERICAL_FILTER_OPERATORS.includes(o as any);
}

export const SeriesLayoutBehaviors = [
  'stacked',
  'grouped',
  'stackedPercent',
] as const;
export type SeriesLayoutBehavior = (typeof SeriesLayoutBehaviors)[number];

export const ChartSortOrder = makeBetterEnum('byMetricValue', 'byDimension');
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type ChartSortOrder =
  (typeof ChartSortOrder)[keyof typeof ChartSortOrder];
